import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  FileText, Upload, Send, Loader2, AlertTriangle, CheckCircle,
  DollarSign, Clock, Shield, TrendingUp, MessageSquare, CalendarCheck
} from 'lucide-react';

const RISK_COLOR = (score) => {
  if (!score) return 'border-slate-200 text-slate-500';
  if (score >= 70) return 'border-red-300 text-red-700 bg-red-50';
  if (score >= 40) return 'border-amber-300 text-amber-700 bg-amber-50';
  return 'border-green-300 text-green-700 bg-green-50';
};

const RISK_LABEL = (score) => {
  if (!score) return 'N/A';
  if (score >= 70) return 'High';
  if (score >= 40) return 'Medium';
  return 'Low';
};

function StatCard({ title, value, icon: Icon, color, sub }) {
  return (
    <Card className="bg-white border border-slate-200 rounded-sm shadow-none hover:border-slate-300 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{title}</p>
            <p className="font-mono text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
          </div>
          <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${color}`}>
            <Icon className="h-4 w-4 text-white stroke-[1.5px]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ContractAnalyticsPage() {
  const [stats, setStats] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [obligations, setObligations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [contractName, setContractName] = useState('');
  const [question, setQuestion] = useState('');
  const [qaLoading, setQaLoading] = useState(false);
  const [qaAnswer, setQaAnswer] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [s, c, o] = await Promise.all([
        api.get('/contracts/stats'),
        api.get('/contracts'),
        api.get('/contracts/obligations/upcoming'),
      ]);
      setStats(s.data);
      setContracts(c.data);
      setObligations(o.data);
      if (c.data.length > 0 && !selected) loadDetail(c.data[0].id);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadDetail = async (id) => {
    try {
      const { data } = await api.get(`/contracts/${id}`);
      setSelected(data);
      setQaAnswer(null);
    } catch (e) { console.error(e); }
  };

  const handleUpload = async () => {
    const fileInput = document.getElementById('spa-file-input');
    if (!fileInput?.files?.[0] || !contractName.trim()) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', fileInput.files[0]);
      form.append('contract_name', contractName);
      await api.post('/contracts/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setContractName('');
      fileInput.value = '';
      await loadData();
    } catch (e) { console.error(e); }
    finally { setUploading(false); }
  };

  const handleAsk = async () => {
    if (!question.trim() || !selected) return;
    setQaLoading(true);
    try {
      const { data } = await api.post(`/contracts/${selected.id}/ask`, { question });
      setQaAnswer(data);
      setQuestion('');
      await loadDetail(selected.id);
    } catch (e) { console.error(e); }
    finally { setQaLoading(false); }
  };

  const handleCompleteObligation = async (obId) => {
    try {
      await api.post(`/contracts/obligations/${obId}/complete`);
      await loadData();
      if (selected) await loadDetail(selected.id);
    } catch (e) { console.error(e); }
  };

  const terms = selected?.terms || [];
  const groupedTerms = terms.reduce((acc, t) => {
    const cat = t.term_category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  const riskFactors = selected?.risk_factors || {};

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6" data-testid="contract-analytics-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="contract-analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Contract Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">SPA contract intelligence, risk scoring & obligation tracking</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="contract-kpis">
        <StatCard title="Active Contracts" value={stats?.active_contracts || 0} icon={FileText} color="bg-[#0A2463]" sub={`${(stats?.total_acq_volume || 0).toLocaleString()} total ACQ`} />
        <StatCard title="Upcoming Obligations" value={stats?.upcoming_obligations || 0} icon={Clock} color="bg-[#F59E0B]" sub="Due within 30 days" />
        <StatCard title="Overdue" value={stats?.overdue_obligations || 0} icon={AlertTriangle} color="bg-[#E11D48]" sub="Past deadline" />
        <StatCard title="Avg Risk Score" value={stats?.avg_risk_score || 0} icon={Shield} color="bg-[#16A34A]" sub="Portfolio average" />
      </div>

      {/* Upload */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
        <CardContent className="p-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Contract Name</label>
              <Input
                value={contractName}
                onChange={(e) => setContractName(e.target.value)}
                placeholder="e.g. Shell Eastern SPA 2024"
                className="rounded-sm mt-1"
                data-testid="spa-name-input"
              />
            </div>
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">SPA Document</label>
              <input
                id="spa-file-input"
                type="file"
                accept=".pdf,.txt,.doc,.docx"
                className="mt-1 text-sm text-slate-500 file:mr-4 file:py-2 file:px-3 file:rounded-sm file:border file:border-slate-200 file:text-xs file:font-medium file:bg-white file:text-slate-700 hover:file:bg-slate-50"
                data-testid="spa-file-input"
              />
            </div>
            <Button
              onClick={handleUpload}
              disabled={uploading || !contractName.trim()}
              data-testid="spa-upload-button"
              className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              {uploading ? 'Processing...' : 'Upload & Analyze'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="contracts">
        <TabsList className="rounded-sm h-9 bg-slate-100 p-0.5">
          <TabsTrigger value="contracts" className="rounded-sm text-xs flex-1" data-testid="tab-contracts">
            <FileText className="h-3 w-3 mr-1" />Contracts ({contracts.length})
          </TabsTrigger>
          <TabsTrigger value="obligations" className="rounded-sm text-xs flex-1" data-testid="tab-obligations">
            <CalendarCheck className="h-3 w-3 mr-1" />Obligations ({obligations.length})
          </TabsTrigger>
        </TabsList>

        {/* Contracts Tab */}
        <TabsContent value="contracts">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: Contract List */}
            <div className="lg:col-span-3 space-y-2">
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 px-1">
                Contracts ({contracts.length})
              </p>
              {contracts.length === 0 ? (
                <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                  <CardContent className="py-12 text-center">
                    <FileText className="h-8 w-8 mx-auto text-slate-200 stroke-[1px]" />
                    <p className="mt-3 text-xs text-slate-400">No contracts yet</p>
                    <p className="text-[10px] text-slate-300">Upload an SPA to begin</p>
                  </CardContent>
                </Card>
              ) : (
                contracts.map(c => (
                  <Card
                    key={c.id}
                    onClick={() => loadDetail(c.id)}
                    data-testid={`contract-card-${c.contract_name?.replace(/\s/g, '-')}`}
                    className={`cursor-pointer transition-all rounded-sm shadow-none border ${
                      selected?.id === c.id ? 'border-[#0A2463] bg-[#0A2463]/[0.03]' : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <CardContent className="p-3">
                      <p className="text-xs font-bold text-slate-900 truncate">{c.contract_name}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        {c.counterparty && <span className="text-[10px] text-slate-400 truncate">{c.counterparty}</span>}
                        {c.risk_score != null && (
                          <Badge variant="outline" className={`rounded-sm text-[9px] uppercase ${RISK_COLOR(c.risk_score)}`}>
                            Risk: {c.risk_score}
                          </Badge>
                        )}
                      </div>
                      {c.contract_type && (
                        <Badge variant="outline" className="rounded-sm text-[9px] uppercase mt-1">{c.contract_type}</Badge>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Right: Detail */}
            <div className="lg:col-span-9">
              {!selected ? (
                <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                  <CardContent className="py-24 text-center">
                    <FileText className="h-12 w-12 mx-auto text-slate-200 stroke-[1px]" />
                    <p className="mt-4 text-sm text-slate-400">Select a contract to view details</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {/* Header */}
                  <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h2 className="text-lg font-bold text-slate-900">{selected.contract_name}</h2>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-500">
                            {selected.counterparty && <span>Counterparty: <b className="text-slate-700">{selected.counterparty}</b></span>}
                            {selected.contract_type && <span>Type: <b className="text-slate-700">{selected.contract_type}</b></span>}
                            {selected.effective_date && <span>Effective: <b className="text-slate-700 font-mono">{selected.effective_date}</b></span>}
                            {selected.expiry_date && <span>Expiry: <b className="text-slate-700 font-mono">{selected.expiry_date}</b></span>}
                          </div>
                        </div>
                        <div className="text-right space-y-1">
                          {selected.risk_score != null && (
                            <Badge variant="outline" className={`rounded-sm text-xs ${RISK_COLOR(selected.risk_score)}`}>
                              Risk: {selected.risk_score} ({RISK_LABEL(selected.risk_score)})
                            </Badge>
                          )}
                          {selected.status && (
                            <Badge variant="outline" className={`rounded-sm text-[9px] uppercase ml-2 ${
                              selected.status === 'active' ? 'border-green-300 text-green-700 bg-green-50' : 'border-slate-200'
                            }`}>
                              {selected.status}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {selected.pricing_formula && (
                        <div className="mt-3 px-3 py-2 bg-slate-50 border border-slate-100 rounded-sm">
                          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Pricing Formula</p>
                          <p className="text-xs font-mono text-slate-800 mt-0.5">{selected.pricing_formula}</p>
                        </div>
                      )}
                      <div className="flex gap-4 mt-3 text-xs text-slate-500">
                        {selected.acq_volume && <span>ACQ: <b className="font-mono text-slate-700">{selected.acq_volume} {selected.acq_unit}</b></span>}
                        {selected.dqt_pct && <span>DQT: <b className="font-mono text-slate-700">{selected.dqt_pct}%</b></span>}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Risk Factors */}
                  {Object.keys(riskFactors).length > 0 && (
                    <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                          <Shield className="h-4 w-4 text-[#E11D48]" />Risk Factors
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 pt-0">
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                          {Object.entries(riskFactors).map(([key, val]) => (
                            <div key={key}>
                              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{key.replace(/_/g, ' ')}</p>
                              <div className="flex items-center gap-1.5 mt-1">
                                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${val >= 70 ? 'bg-red-500' : val >= 40 ? 'bg-amber-500' : 'bg-green-500'}`}
                                    style={{ width: `${val}%` }}
                                  />
                                </div>
                                <span className="font-mono text-[10px] font-bold text-slate-700 w-6 text-right">{val}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Terms & Q&A Tabs */}
                  <Tabs defaultValue="terms">
                    <TabsList className="w-full rounded-sm h-9 bg-slate-100 p-0.5">
                      <TabsTrigger value="terms" className="rounded-sm text-xs flex-1" data-testid="tab-spa-terms">
                        Extracted Terms ({terms.length})
                      </TabsTrigger>
                      <TabsTrigger value="contract-obligations" className="rounded-sm text-xs flex-1" data-testid="tab-contract-obligations">
                        <CalendarCheck className="h-3 w-3 mr-1" />Obligations ({selected.obligations?.length || 0})
                      </TabsTrigger>
                      <TabsTrigger value="ask" className="rounded-sm text-xs flex-1" data-testid="tab-spa-ask">
                        <MessageSquare className="h-3 w-3 mr-1" />Ask the Contract
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="terms">
                      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                        <CardContent className="p-4 space-y-4">
                          {Object.entries(groupedTerms).map(([category, catTerms]) => (
                            <div key={category}>
                              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">
                                {category.replace(/_/g, ' ')}
                              </p>
                              <div className="space-y-1.5">
                                {catTerms.map(t => (
                                  <div key={t.id} className="flex items-center gap-3 px-3 py-2 border border-slate-100 rounded-sm">
                                    <span className="text-xs font-medium text-slate-700 w-36 truncate">{t.term_key}</span>
                                    <span className="text-xs font-mono text-slate-900 flex-1">
                                      {typeof t.term_value === 'object' ? JSON.stringify(t.term_value) : String(t.term_value)}
                                    </span>
                                    {t.confidence_score && (
                                      <Badge variant="outline" className={`rounded-sm text-[9px] ${
                                        parseFloat(t.confidence_score) >= 0.9 ? 'border-green-300 text-green-700 bg-green-50' :
                                        parseFloat(t.confidence_score) >= 0.7 ? 'border-amber-300 text-amber-700 bg-amber-50' :
                                        'border-red-300 text-red-700 bg-red-50'
                                      }`}>
                                        {(parseFloat(t.confidence_score) * 100).toFixed(0)}%
                                      </Badge>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                          {terms.length === 0 && (
                            <div className="py-8 text-center text-sm text-slate-400">No extracted terms yet</div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="contract-obligations">
                      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                        <CardContent className="p-0">
                          {(!selected.obligations || selected.obligations.length === 0) ? (
                            <div className="py-12 text-center text-sm text-slate-400">No obligations extracted</div>
                          ) : (
                            <table className="w-full text-sm" data-testid="contract-obligations-table">
                              <thead>
                                <tr className="border-b border-slate-100">
                                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Type</th>
                                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Description</th>
                                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Deadline</th>
                                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Status</th>
                                  <th className="px-5 py-2"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {selected.obligations.map(ob => {
                                  const overdue = ob.status === 'pending' && ob.deadline && new Date(ob.deadline) < new Date();
                                  return (
                                    <tr key={ob.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${overdue ? 'bg-red-50/30' : ''}`}>
                                      <td className="px-5 py-2">
                                        <Badge variant="outline" className="rounded-sm text-[9px] uppercase">{ob.obligation_type}</Badge>
                                      </td>
                                      <td className="px-5 py-2 text-xs text-slate-700 max-w-[300px]">{ob.description}</td>
                                      <td className="px-5 py-2 text-xs font-mono text-slate-600">
                                        {ob.deadline ? new Date(ob.deadline).toLocaleDateString() : '—'}
                                        {overdue && <span className="ml-1 text-red-600 font-bold">(overdue)</span>}
                                      </td>
                                      <td className="px-5 py-2">
                                        <Badge variant="outline" className={`rounded-sm text-[9px] uppercase ${
                                          ob.status === 'completed' ? 'border-green-300 text-green-700 bg-green-50' :
                                          overdue ? 'border-red-300 text-red-700 bg-red-50' :
                                          'border-amber-300 text-amber-700 bg-amber-50'
                                        }`}>
                                          {overdue ? 'overdue' : ob.status}
                                        </Badge>
                                      </td>
                                      <td className="px-5 py-2">
                                        {ob.status === 'pending' && (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleCompleteObligation(ob.id)}
                                            data-testid={`complete-obligation-${ob.id}`}
                                            className="rounded-sm text-[10px] h-6 border-green-200 text-green-700 hover:bg-green-50"
                                          >
                                            <CheckCircle className="h-3 w-3 mr-1" />Complete
                                          </Button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="ask">
                      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                        <CardContent className="p-4 space-y-4">
                          <div className="flex gap-2">
                            <Input
                              value={question}
                              onChange={(e) => setQuestion(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
                              placeholder="e.g. What is the pricing formula? What are the DQT provisions?"
                              className="rounded-sm flex-1"
                              data-testid="spa-question-input"
                            />
                            <Button
                              onClick={handleAsk}
                              disabled={qaLoading || !question.trim()}
                              data-testid="spa-ask-button"
                              className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white"
                            >
                              {qaLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            </Button>
                          </div>

                          {qaAnswer && (
                            <div className="p-4 border border-slate-200 rounded-sm bg-slate-50" data-testid="spa-qa-answer">
                              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Answer</p>
                              <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{qaAnswer.answer}</p>
                            </div>
                          )}

                          {selected.qa_history?.length > 0 && (
                            <div>
                              <Separator className="my-3" />
                              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Previous Questions</p>
                              <div className="space-y-2">
                                {selected.qa_history.map(q => (
                                  <div key={q.id} className="p-3 border border-slate-100 rounded-sm">
                                    <p className="text-xs font-medium text-slate-700">{q.question}</p>
                                    <p className="text-xs text-slate-500 mt-1 line-clamp-3">{q.answer}</p>
                                    <p className="text-[10px] text-slate-300 mt-1 font-mono">
                                      {new Date(q.created_at).toLocaleString()}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Obligations Tab */}
        <TabsContent value="obligations">
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <CalendarCheck className="h-4 w-4 text-[#F59E0B]" />Upcoming Obligations
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {obligations.length === 0 ? (
                <div className="py-12 text-center text-sm text-slate-400">No upcoming obligations</div>
              ) : (
                <table className="w-full text-sm" data-testid="obligations-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Contract</th>
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Counterparty</th>
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Type</th>
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Description</th>
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Deadline</th>
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Status</th>
                      <th className="px-5 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {obligations.map(ob => {
                      const overdue = ob.status === 'pending' && ob.deadline && new Date(ob.deadline) < new Date();
                      return (
                        <tr key={ob.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${overdue ? 'bg-red-50/30' : ''}`}>
                          <td className="px-5 py-2 text-xs font-medium text-slate-800">{ob.contract_name}</td>
                          <td className="px-5 py-2 text-xs text-slate-600">{ob.counterparty || '—'}</td>
                          <td className="px-5 py-2">
                            <Badge variant="outline" className="rounded-sm text-[9px] uppercase">{ob.obligation_type}</Badge>
                          </td>
                          <td className="px-5 py-2 text-xs text-slate-700 max-w-[250px] truncate">{ob.description}</td>
                          <td className="px-5 py-2 text-xs font-mono text-slate-600">
                            {ob.deadline ? new Date(ob.deadline).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-5 py-2">
                            <Badge variant="outline" className={`rounded-sm text-[9px] uppercase ${
                              overdue ? 'border-red-300 text-red-700 bg-red-50' : 'border-amber-300 text-amber-700 bg-amber-50'
                            }`}>
                              {overdue ? 'overdue' : ob.status}
                            </Badge>
                          </td>
                          <td className="px-5 py-2">
                            {ob.status === 'pending' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCompleteObligation(ob.id)}
                                className="rounded-sm text-[10px] h-6 border-green-200 text-green-700 hover:bg-green-50"
                              >
                                <CheckCircle className="h-3 w-3 mr-1" />Done
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
